<template>
  <div class="chat-box" :class="{ wide: singleScreen }">
    <div class="c-header-wrap c-flex ac w100">
      <div v-if="talk" class="c-talk-icon c-cover-40" @click="showMembers = !showMembers">
        <c-img :src="coverPath" />
      </div>
      <div class="s">
        <div class="c-talk-name" :class="{ noselect: !talk }" :style="maxNameWidth" @click="showMembers = !showMembers">
          {{ talkName }}
        </div>
      </div>
      <div class="c-btn-edit">
        <v-tooltip bottom>
          <v-btn
            slot="activator"
            class="c-edit-btn"
            flat
            icon
            color="clr-black"
            @click="editTalkDialog = true"
            :disabled="isGuest || !isEditable"
          >
            <v-icon>mdi-pencil</v-icon>
          </v-btn>
          <span>{{ $T('talk.setting.title') }}</span>
        </v-tooltip>
      </div>
      <div class="c-btn-exit">
        <template v-if="!talk || talk.type == 1 || !talk.is_creator">
          <v-tooltip bottom>
            <v-btn
              slot="activator"
              class="c-edit-btn"
              flat
              icon
              color="clr-black"
              @click="exitConfirm = true"
              :disabled="isGuest || !talk"
            >
              <v-icon>mdi-logout</v-icon>
            </v-btn>
            <span>{{ $T('talk.exit.title') }}</span>
          </v-tooltip>
        </template>
        <template v-else>
          <v-tooltip bottom>
            <v-btn slot="activator" class="c-edit-btn" flat icon color="clr-black" @click="deleteConfirm = true">
              <v-icon>mdi-delete-outline</v-icon>
            </v-btn>
            <span>{{ $T('talk.delete.title') }}</span>
          </v-tooltip>
        </template>
      </div>
      <div v-if="singleScreen" class="c-btn-menu">
        <v-tooltip bottom>
          <v-btn slot="activator" flat icon color="clr-black" @click="$router.push({ name: 'topTalk' })">
            <v-icon>mdi-menu</v-icon>
          </v-btn>
          <span>{{ $T('common.list') }}</span>
        </v-tooltip>
      </div>
      <div class="c-members" v-if="talk && members" v-show="showMembers">
        <selected-members
          :cid="cid"
          :memberGroups="members.keys"
          :creatorCmid="talk.cmid"
          :retiredCmids="members.retires"
          type="talk"
          :typeId="talkId"
          :friends="curFriends"
        />
      </div>
    </div>
    <div class="msgs-loading" v-if="requestedNum <= lowestNum && lowestNum > 1">
      <v-progress-circular :width="2" :size="20" color="clr-black" indeterminate></v-progress-circular>
    </div>

    <div class="msgs-area">
      <div>
        <template v-if="talk">
          <template v-for="dailyData in msgs">
            <div>
              <div class="c-date-line sticky">{{ fmtDate(dailyData.ymd) }}</div>
              <template v-for="(msg, idx) in dailyData.msgs">
                <div v-if="isReadLine(dailyData.msgs, idx)" class="c-unread-line">
                  <v-divider></v-divider>
                  <div class="c-unread-msg">{{ $T('talk.unread_line') }}</div>
                  <v-divider></v-divider>
                </div>
                <div class="c-msg-area" :class="isSelf(msg.cmid) ? 'self' : 'other'">
                  <div class="c-avatar" v-if="!isSelf(msg.cmid)">
                    <cmid-avatar v-if="!!cms" :cmid="msg.cmid" :cms="cms" :cid="talk.cid" :size="30" />
                  </div>
                  <div>
                    <div v-if="talk.type == 0 && !isSelf(msg.cmid) && !!cms" class="c-name">
                      <cm-name
                        :cid="talk.cid"
                        :cmid="msg.cmid"
                        type="talk"
                        :typeId="talk.id"
                        :cms="cms"
                        :friends="curFriends"
                      />
                    </div>
                    <talk-msg :talkMsg="msg" :tid="talkId" :maxWidth="maxBoxWidth" />
                  </div>
                  <div class="c-msg-append">
                    <div class="c-read-count">
                      <talk-read
                        v-if="isSelf(msg.cmid) && read && otherCmids"
                        :msg="msg"
                        :read="read"
                        :cmids="otherCmids"
                      />
                    </div>
                    <div class="c-time">{{ fmtTime(msg.tstamp) }}</div>
                  </div>
                </div>
              </template>
            </div>
          </template>
          <div v-if="nodata" class="c-none">
            <template v-if="lowestNum == -1">
              <spinner></spinner>
            </template>
            <template v-else>
              {{ $T('talk.nomsg') }}
            </template>
          </div>
        </template>
      </div>
    </div>

    <div class="chat-input-area" :class="{ 'no-attach': !canShare }">
      <div class="c-flex w100 as">
        <!-- POST(MAIL)/event create menu, anchored above the + button (same pattern as PostList).
             Hidden for general members of a publicity_member=0 community (canShare). -->
        <v-menu v-if="canShare" top offset-y :disabled="isGuest || !talk || sending" :nudge-top="6">
          <v-btn
            slot="activator"
            class="c-attach-btn"
            flat
            icon
            color="clr-btn"
            :disabled="isGuest || !talk || sending"
          >
            <v-icon>mdi-plus-circle-outline</v-icon>
          </v-btn>
          <edit-post-menu-list
            v-if="talk"
            :initCid="talk.cid"
            :initMembers="talkMemberKeys"
            :talkShare="{ tid: talk.id }"
            :includeEvent="true"
            @eventSelected="onEventSelected"
          />
        </v-menu>
        <v-btn
          v-if="canShare"
          class="c-attach-btn c-attach-btn-last"
          flat
          icon
          color="clr-btn"
          @click="onAttachClick"
          :disabled="isGuest || !talk || sending"
        >
          <v-icon>mdi-image-plus</v-icon>
        </v-btn>
        <div class="s">
          <v-textarea
            auto-grow
            rows="1"
            solo
            class="c-input"
            name="inputMsg"
            v-model="inputMsg"
            :disabled="isGuest || !talk"
            :loading="sending"
          ></v-textarea>
        </div>
        <v-btn
          class="c-send-btn"
          slot="activator"
          flat
          icon
          color="clr-btn"
          @click="sendMsg"
          :disabled="isGuest || !talk || !inputMsg || sending"
          :loading="sending"
        >
          <v-icon>mdi-send</v-icon>
        </v-btn>
      </div>
    </div>

    <edit-talk v-if="editTalkDialog" :talk="talk" @close="editTalkDialog = false" />

    <confirm-dialog
      v-if="deleteConfirm"
      ref="deleteConfirm"
      :open="deleteConfirm"
      :dialogKey="'delete'"
      :confirmValue="1"
      :persistent="false"
      danger
      :dialogTitle="$T('talk.delete.title')"
      :dialogText="$T('talk.delete.confirm')"
      @ok="onDeleteOk"
      @cancel="deleteConfirm = false"
    />

    <confirm-dialog
      v-if="exitConfirm"
      ref="exitConfirm"
      :open="exitConfirm"
      :dialogKey="'exit'"
      :confirmValue="1"
      :persistent="false"
      danger
      :dialogTitle="$T('talk.exit.title')"
      :dialogText="$T('talk.exit.confirm')"
      @ok="onExitOk"
      @cancel="exitConfirm = false"
    />

    <edit-event-dialog
      v-if="editEventDialog"
      :calEvent="editEventData.calEvent"
      :members="editEventData.members"
      :targetMoment="editEventData.targetMoment"
      :readOnly="true"
      @close="editEventDialog = false"
    ></edit-event-dialog>

    <!-- Media (image / video) upload form: the same Dropzone file-selection + progress widget the -->
    <!-- album page uses. On completion the files are sent into this talk as an album message. -->
    <talk-upload-dialog
      v-if="uploadDialog && talk"
      :cid="talk.cid"
      :tid="talk.id"
      :members="talkMemberKeys"
      v-model="uploadDialog"
      @sent="onMediaSent"
    />

    <!-- Calendar event create (from the + menu). On register it is shared into this talk via -->
    <!-- msg-event (see EditEventDialog.talkShare). -->
    <edit-event-dialog
      v-if="eventCreateDialog && talk"
      :initCid="talk.cid"
      :members="talkMemberKeys"
      :talkShare="{ tid: talk.id }"
      @close="eventCreateDialog = false"
    ></edit-event-dialog>
  </div>
</template>

<script>
import editTalkDialog from './EditTalkDialog.vue';
import talkRead from './TalkRead.vue';
import talkMsg from './TalkMsg.vue';

import { CONST } from '../../constant';
import App from '../../utils/App';
import UTILS from '../../utils/utils';
import { getMsgs, getMembers, addMsg, saveRead, exitFromTalk, deleteTalk } from '../../api/communityTalk';
import CacheUtils from '../../utils/CacheUtils';
import db from '../../firebase/init';
import talkPlugin from '../../utils/plugin/talkPlugin';
import { mapGetters, mapActions } from 'vuex';

import editEventDialog from '../event/EditEventDialog';
import talkUploadDialog from './TalkUploadDialog.vue';
import editPostMenuList from '../post/EditPostMenuList.vue';

export default {
  components: {
    'edit-talk': editTalkDialog,
    'talk-read': talkRead,
    'talk-msg': talkMsg,
    'edit-event-dialog': editEventDialog,
    'talk-upload-dialog': talkUploadDialog,
    'edit-post-menu-list': editPostMenuList,
  },
  mixins: [talkPlugin],
  props: {
    talkList: { type: Array, required: true },
    singleScreen: { type: Boolean, required: true },
    friends: { type: Object, required: false },
  },
  data() {
    return {
      editTalkDialog: false,
      uploadDialog: false,
      eventCreateDialog: false,
      inputMsg: null,
      msgs: [],
      members_from_server: false,
      members: null,
      otherCmids: null,
      sending: false,
      readNum: 0,
      read: {},
      othreCmids: null,
      requestedNum: -1,
      lowestNum: -1,
      initTalkId: 0,
      scrollYExists: false,
      saveReadNumNextEnd: false,
      savingReadNum: -1,
      scrollChecking: false,
      // scroll percent 0-100
      scrollPercent: 0,
      // scroll bar top px
      scrollPosition: 0,
      exitConfirm: 0,
      deleteConfirm: 0,
      showMembers: false,
      cms: null,
      scrollEvent: _.throttle(
        () => {
          this.calcScroll();
        },
        500,
        { leading: true, trailing: true }
      ),
      curFriends: this.friends || {},
      editEventDialog: false,
      editEventData: {},
    };
  },
  created() {
    if (this.talk) {
      if (this.routeTalkId && this.talk.id != this.routeTalkId) {
        this.$store.commit('TALK_SELECT', this.routeTalkId);
        this.setTalkListener();
        return;
      }
      this.loadMsgs(this.talk, this.talk.last_num, CONST.talk.load_count, true);
      this.initReadNum(this.talk);
    } else if (this.routeTalkId) {
      this.$store.commit('TALK_SELECT', this.routeTalkId);
      this.setTalkListener();
    } else if (this.singleScreen) {
      this.$router.push({ name: 'topTalk' });
    }
  },
  mounted() {
    var self = this;
    if (this.isAndroid) {
      var ele = this.msgsArea;
      ele.onscroll = this.scrollEvent;
    } else {
      $('.msgs-area').mCustomScrollbar({
        theme: 'minimal',
        axis: 'y',
        scrollbarPosition: 'outside',
        advanced: {
          updateOnContentResize: true,
          updateOnBrowserResize: true,
        },
        callbacks: {
          onOverflowY: function() {
            self.scrollYExists = true;
          },
          onOverflowYNone: function() {
            self.scrollYExists = false;
          },
          whileScrolling: function() {
            self.scrollPercent = this.mcs.topPct;
            self.scrollPosition = this.mcs.draggerTop;
          },
        },
      });
    }
    if (this.talk) this.resetMembers(this.talk);
    if (this.cid) {
      var self = this;
      this.getAllGroupsAndMembers(this.cid, function(data) {
        self.cms = data.cms;
      });
    }
  },
  beforeDestroy() {
    this.$store.commit('TALK_SELECT', null);
    this.setTalkListener();
    var ele = this.msgsArea;
    if (!ele) return;
    ele.removeEventListener('scroll', this.scrollEvent);
  },
  watch: {
    h(n, o) {
      this.checkScroll();
    },
    cid(n, o) {
      this.cms = null;
      if (n) {
        var self = this;
        this.getAllGroupsAndMembers(n, function(data) {
          self.cms = data.cms;
        });
      }
    },
    latestTalk(n, o) {
      if (!n || !this.talk || this.talk.id != n.talk_id) return;
      this.mergeMsg(n);
      this.moveToEndIfRequired(n);
    },
    latestRead(n, o) {
      if (!n || !this.talk || this.talk.id != n.talk_id) return;
      this.read = n;
    },
    selectedCommunity(n, o) {
      var cid = n ? n.id : 0;
      if (cid && this.talk && this.talk.cid != cid) {
        this.$store.commit('TALK_SELECT', 0);
        this.setTalkListener();
      }
    },
    talkList(n, o) {
      if (!n || n.length == 0) {
        if (this.routeTalkId) this.$router.push({ name: 'topTalk' });
      } else {
        this.$nextTick(function() {
          if (this.routeTalkId) {
            this.$store.commit('TALK_SELECT', this.routeTalkId);
            this.setTalkListener();
          } else {
            this.$store.commit('TALK_SELECT', n[0].id);
            this.setTalkListener();
          }
        });
      }
    },
    scrollPosition(n, o) {
      if (this.scrollChecking || this.lowestNum <= 1) return;
      if (n < 100) {
        this.scrollChecking = true;
        console.log('scroll check start!');
        var self = this;
        setTimeout(function() {
          self.scrollChecking = false;
          console.log(`scroll check result ${self.scrollPosition}!`);
          if (self.scrollPosition < 100) self.loadMore();
        }, 500);
      }
    },
    scrollPercent(n, o) {
      if (n > 95) this.saveRead();
    },
    talk(n, o) {
      this.clearAll();
      if (!n) return;
      this.resetMembers(n);
      this.$nextTick(function() {
        this.initReadNum(n);
        this.loadMsgs(n, n.last_num, CONST.talk.load_count, true);
      });
      // this.onModifyMember();
    },
    stateFirebaseInitialized(n, o) {
      if (!n) return;
    },
    stateCalUpdate(n, o) {
      if (!n) return;
      this.openEditEventDialog(n);
      this.$nextTick(function() {
        this.$store.commit('CAL_UPDATE', null);
      });
    },
  },
  computed: {
    cid() {
      return this.talk ? this.talk.cid : 0;
    },
    // Talk participant keys (m<cmid>) the uploaded media are shared with.
    talkMemberKeys() {
      return this.members && this.members.keys ? this.members.keys : [];
    },
    // Hide the share (+) and image-add affordances for a general member of a publicity_member=0
    // ("member-to-member direct interaction OFF") community (server also rejects such targets).
    canShare() {
      return !!this.talk && App.canShareInCommunity(this.talk.cid);
    },
    latestTalk() {
      return this.$store.state.talk.latest;
    },
    latestRead() {
      return this.$store.state.talk.latestRead;
    },
    onTotalScroll() {
      this.saveRead();
    },
    routeTalkId() {
      return this.$route.params.talk_id ? Number(this.$route.params.talk_id) : 0;
    },
    stateFirebaseInitialized() {
      return this.$store.state.tools.firebaseInitialized;
    },
    talkId() {
      return this.talk ? this.talk.id : 0;
    },
    talk() {
      return this.$store.state.talk.talk;
    },
    talkName() {
      return this.getTalkName(this.talk, this.cms);
    },
    coverPath() {
      return this.getTalkCoverPath(this.talk, this.cms);
    },
    talkReadNum() {
      return this.talk ? this.talk.read_num : 0;
    },
    maxNameWidth() {
      var w = 300;
      if (this.geMax) w = 358;
      else if (this.gePc) w = this.w - 550 - 207;
      else if (this.leSp) w = this.w - 187;
      else if (this.leTbS) w = this.w - 307;
      else if (this.leTb) w = this.w - 250 - 207;
      return `max-width: ${w}px;`;
    },
    maxBoxWidth() {
      var w = 300;
      if (this.geMax) w = 400;
      else if (this.gePc) w = this.w - 550 - 125;
      else if (this.leSp) w = this.w - 95;
      else if (this.leTbS) w = this.w - 220 - 100;
      else if (this.leTb) w = this.w - 250 - 125;
      return w;
    },
    isEditable() {
      if (!this.talk || this.talk.type == 1 || !this.isEditableCid(this.talk.cid)) return false;
      return this.talk.is_creator || this.talk.editable;
    },
    nodata() {
      return this.talk && this.msgs.length == 0;
    },
    msgsArea() {
      return this.$el.querySelector('.msgs-area');
    },
    stateCalUpdate() {
      return this.$store.state.calendar.update;
    },
  },
  methods: {
    cm(cmid) {
      const cm = this.cms[cmid];
      if (cm) return cm;
      return this.curFriends ? this.curFriends[cmid] : null;
    },
    clearAll() {
      this.editTalkDialog = false;
      this.inputMsg = null;
      this.msgs = [];
      this.members_from_server = false;
      this.members = null;
      this.sending = false;
      this.readNum = 0;
      this.requestedNum = -1;
      this.lowestNum = -1;
      this.initTalkId = 0;
    },
    isTalkChanged(talk) {
      return !this.talk || this.talk.id != talk.id;
    },
    initReadNum(talk) {
      if (talk.last_num == talk.read_num) return (this.readNum = -1);
      if (talk.last_num == 0 && talk.read_num == null) {
        this.readNum = 0;
        this.saveRead(talk);
        return;
      }
      this.readNum = talk.read_num;
    },
    loadMsgs(talk, offset, count, init) {
      if (this.isTalkChanged(talk)) return;
      if (init) {
        if (this.initTalkId == talk.id) return;
        this.msgs = [];
        this.initTalkId = talk.id;
        this.requestNum == -1;
        this.saveReadNumNextEnd = true;
        this.savingReadNum = -1;
      }
      if (this.requestedNum == offset) return;
      this.requestedNum = offset;

      if (!offset) offset = 0;
      if (offset - count < 0) count = offset;
      if (count == 0) {
        console.warn(`requested count ${count} is invalid`);
        return (this.lowestNum = 0);
      }
      console.log(`load start from ${offset}-${offset - count}`);
      this.getMsgs(talk, offset, count, init);
    },
    loadMore() {
      if (!this.talk) return;
      if (this.lowestNum <= 1) return console.log(`skip load lowestNum=${this.lowestNum}`);
      this.loadMsgs(this.talk, this.lowestNum, CONST.talk.load_count, false);
    },
    getMsgs(talk, offset, count, init) {
      const start = Math.floor(offset / 100);
      const mod = offset % 100;
      let lastCount = count;

      const self = this;
      for (var i = start; i >= 0; i--) {
        const curOffset = i * 100 + (i == start ? mod : 99);
        let getCount = i == start ? mod + 1 : 100;
        if (getCount > lastCount) getCount = lastCount;
        this.getMsgsFromCache(talk, i, curOffset, getCount, init);
        lastCount -= getCount;
        if (lastCount <= 0) break;
      }
    },
    getMsgsFromCache(talk, i, offset, count, init) {
      var cacheKey = `msgs-${talk.id}-${i}`;
      var self = this;
      this.$commin.clientdb.getObject(cacheKey, function(data) {
        self.loadMsgsFromCacheData(talk, data, offset, count, cacheKey, init);
      });
    },
    loadMsgsFromCacheData(talk, cachedMsgs, offset, count, cacheKey, init) {
      if (this.isTalkChanged(talk)) return;
      if (!cachedMsgs) return this.getMsgsFromServer(talk, offset, count, init);

      var hasAll = true;
      try {
        var end = offset - count;
        if (end < 0) end = 0;
        for (var i = offset; i > end; i--) {
          var msgs = cachedMsgs[`m${i}`];
          if (msgs && Object.keys(msgs).length > 0) {
            for (var k of Object.keys(msgs)) if (!msgs[k].dummy) this.mergeMsg(msgs[k]);
          } else {
            console.log(`there is no data ${i} in ${cacheKey}`);
            hasAll = false;
          }
        }
        if (init) this.forceMoveToEnd();
      } catch (e) {
        console.error(`cache data ${cacheKey} will be broken.`, e);
        this.$commin.clientdb.deleteObject(cacheKey);
        this.getMsgsFromServer(talk, offset, count, init);
        return;
      }
      if (!hasAll) this.getMsgsFromServer(talk, offset, count, init);
    },
    getMsgsFromServer(talk, offset, count, init) {
      (async () => {
        // get from server
        console.log(`get data from server ${offset}-${offset - count}`);
        try {
          const response = await getMsgs(talk.id, offset, count);
          const msgs = response.data;
          if (this.isTalkChanged(talk)) return this.saveCaches(talk.id, msgs, offset, count);
          for (var msg of msgs) if (!msg.dummy && !msg.idx) this.mergeMsg(msg);
          if (init) this.forceMoveToEnd();
          this.saveCaches(talk.id, msgs, offset, count);
        } catch (e) {
          console.error(e);
          this.$store.commit('OPEN_SNACKBAR', { msg: $T('talk.setting.load.failed'), danger: true });
        }
      })();
    },
    saveCaches(tid, msgs, offset, count) {
      if (!msgs || msgs.length == 0) return;
      const msgOptionMap = {};
      const msgMap = {};
      for (const msg of msgs) {
        if (msg.idx) {
          const key = `${tid}_${msg.num}`;
          const l = msgOptionMap[key] || [];
          l.push(msg);
          msgOptionMap[key] = l;
        } else {
          const key = `m${msg.num}`;
          const l = msgMap[key] || [];
          l.push(msg);
          msgMap[key] = l;
        }
      }

      for (const key of Object.keys(msgOptionMap)) {
        this.saveTalkOptionMsgs(key, msgOptionMap[key]);
      }

      const start = offset;
      const end = offset - count;
      for (let i = start; i >= end; i--) {
        if (!msgMap[`m${i}`]) {
          console.log(`add dummy to ${tid}-${i}`);
          msgMap[`m${i}`] = [{ cmid: 0, dummy: 1 }];
        }
      }

      const startIdx = Math.floor(offset / 100);
      const endIdx = Math.floor((offset - count) / 100);
      for (let i = startIdx; i >= endIdx; i--) this.saveCacheMap(tid, i, msgMap, offset, count);
    },
    async saveTalkOptionMsgs(key, msgs) {
      const m = (await CacheUtils.getTalkOptionMsgs(key)) || {};
      for (const msg of msgs) m[`${msg.cmid}_${msg.idx}`] = JSON.parse(msg.msg);
      await CacheUtils.setTalkOptionMsgs(key, m);
    },
    saveCacheMap(tid, i, msgMap, offset, count) {
      let start = i * 100 + 99;
      if (start > offset) start = offset;
      let end = i * 100;
      if (offset - count > end) end = offset - count;

      const cacheKey = `msgs-${tid}-${i}`;
      (async () => {
        const data = (await CacheUtils.getTalkMsgs(cacheKey)) || {};
        for (let num = start; num >= end; num--) {
          const k = `m${num}`;
          const d = data[k] || {};
          for (const msg of msgMap[k]) d[`c${msg.cmid}`] = msg;
          data[k] = d;
        }
        await CacheUtils.setTalkMsgs(cacheKey, data);
      })();
    },
    mergeMsg(msg) {
      var result = this.execMergeMsg(msg);
      if ((result && this.lowestNum == -1) || this.lowestNum > msg.num) {
        if (msg.num < 1) {
          console.warn(`msg num ${msg.num} is invalid!`);
        } else {
          this.lowestNum = msg.num;
        }
      }
      return result;
    },
    execMergeMsg(msg) {
      if (!msg.num) return true;
      var ymd = Number(this.$moment.unix(msg.tstamp).format('YYYYMMDD'));
      for (var i = this.msgs.length - 1; i >= 0; i--) {
        var dd = this.msgs[i];
        if (ymd > dd.ymd) {
          this.msgs.splice(i + 1, 0, { ymd: ymd, msgs: [msg] });
          return true;
        }
        if (ymd < dd.ymd) {
          if (i == 0 || ymd <= this.msgs[i - 1].ymd) continue;
          this.msgs.splice(i, 0, { ymd: ymd, msgs: [msg] });
          return true;
        }
        // same ymd
        var msgs = this.msgs[i].msgs;
        for (var j = msgs.length - 1; j >= 0; j--) {
          var m = msgs[j];
          if (this.sameMsg(msg, m)) return false;
          if (msg.num > m.num) {
            msgs.splice(j + 1, 0, msg);
            return true;
          }
          if (msg.num < m.num || (msg.num == m.num && msg.cmid < m.cmid)) {
            if (j == 0 || msg.num < msgs[j - 1].num || (msg.num == msgs[j - 1].num && msg.cmid < msgs[j - 1].cmid))
              continue;
            if (this.sameMsg(msg, msgs[j - 1])) return false;
            msgs.splice(j, 0, msg);
            return true;
          }
        }
        msgs.unshift(msg);
        return true;
      }
      this.msgs.unshift({ ymd: ymd, msgs: [msg] });
      return true;
    },

    sameMsg(a, b) {
      return a.num == b.num && a.cmid == b.cmid;
    },
    moveToEndIfRequired(msg) {
      console.log('got new msg');
      if (this.scrollPercent > 90 || this.isSelf(msg.cmid)) {
        this.moveToEnd(300);
        this.saveRead();
      } else if (!this.scrollYExists) {
        this.saveRead(this.talk);
      }
    },
    moveToEnd(d) {
      if (this.saveReadNumNextEnd) {
        this.saveRead();
        this.saveReadNumNextEnd = false;
      }
      this.$nextTick(function() {
        if (this.isAndroid) {
          var ele = this.msgsArea;
          var h = ele.scrollHeight;
          if (d > 0) {
            ele.scrollTo({
              top: h,
              behavior: 'smooth',
            });
          } else {
            ele.scrollTop = h;
          }
        } else {
          $('.msgs-area').mCustomScrollbar('scrollTo', 'bottom', { scrollInertia: d ? d : 0 });
        }
      });
    },
    forceMoveToEnd() {
      this.moveToEnd();
      var self = this;
      setTimeout(() => {
        self.moveToEnd();
      }, 1000);
    },
    resetMembers(talk) {
      if (this.isTalkChanged(talk)) return;

      this.members_from_server = false;
      this.members = null;

      var k = `talk-mems-${talk.id}`;
      var self = this;

      // get first from client db
      $commin.clientdb.getObject(k, function(data) {
        if (!data) self.setMemberData(talk, data, true);
      });

      getMembers(talk.id, true, true)
        .then(function(response) {
          var d = response.data ? response.data : [];
          self.setMemberData(talk, d, true);
          self.$commin.clientdb.putObject(k, d);
        })
        .catch(function(e) {
          console.error('failed to get data', e);
          self.errGetData();
        });
    },
    setMemberData(talk, d, fromServer) {
      if (this.isTalkChanged(talk) || !d) return;
      if (this.members_from_server && !fromServer) return;
      this.members_from_server = fromServer;
      this.members = d;
      this.read = d.read;
      var selfCmid = this.cidToCmid(this.talk.cid);
      var cmids = [];
      for (var key of d.keys) {
        var cmid = Number(key.substring(1));
        if (selfCmid != cmid) cmids.push(cmid);
      }
      this.otherCmids = cmids;
    },
    sendMsg() {
      if (this.isGuest) return;
      var talk = this.talk;
      this.sending = true;
      var data = new FormData();
      data.append('tid', talk.id);
      data.append('msg', this.inputMsg);
      var self = this;
      addMsg(data)
        .then(function(response) {
          var newMsg = response.data;
          self.mergeMsg(newMsg);
          self.updateReadNum(talk, newMsg.num);
          self.$nextTick(function() {
            self.moveToEnd(300);
            self.inputMsg = null;
            self.sending = false;
          });
        })
        .catch(function(error) {
          console.error(error);
          self.shortMsg($T('common.failed.register'), true);
          self.sending = false;
        });
    },
    // Open the media (image / video) upload form for this talk.
    onAttachClick() {
      if (this.isGuest || !this.talk || this.sending) return;
      this.uploadDialog = true;
    },
    // Calendar event selected in the + menu: open the event editor for this talk.
    onEventSelected() {
      if (!this.talk) return;
      this.eventCreateDialog = true;
    },
    // TalkUploadDialog finished uploading and posted the album message; show it in the chat.
    onMediaSent(newMsg) {
      var talk = this.talk;
      if (!talk || !newMsg) return;
      this.mergeMsg(newMsg);
      this.updateReadNum(talk, newMsg.num);
      var self = this;
      this.$nextTick(function() {
        self.moveToEnd(300);
      });
    },
    saveRead(talk) {
      if (this.isGuest) return;
      talk = talk || this.talk;
      if (!talk) return;
      if (talk.last_num == talk.read_num) return console.log(`same read_num ${talk.read_num} skip saving.`);
      if (this.savingReadNum == talk.last_num) return;
      this.savingReadNum = talk.last_num;
      console.log(`save read_num ${talk.last_num}`);
      var data = new FormData();
      data.append('tid', talk.id);
      data.append('num', talk.last_num);
      var self = this;
      saveRead(data)
        .then(function(response) {
          self.updateReadNum(talk, response.data);
        })
        .catch(function(error) {
          console.error(error);
        });
    },
    onExitOk() {
      var talk = this.talk;
      var self = this;
      exitFromTalk(talk.id)
        .then(function(response) {
          self.$store.commit('TALK_REMOVE', talk.id);
          self.$router.push({ name: 'topTalk' });
          self.exitConfirm = false;
        })
        .catch(function(error) {
          console.error(error);
          self.shortMsg($T('common.failed.edit'), true);
          self.exitConfirm = false;
        });
    },
    onDeleteOk() {
      var talk = this.talk;
      var self = this;
      deleteTalk(talk.id)
        .then(function(response) {
          self.$store.commit('TALK_REMOVE', talk.id);
          self.$router.push({ name: 'topTalk' });
          self.deleteConfirm = false;
        })
        .catch(function(error) {
          console.error(error);
          self.shortMsg($T('common.failed.edit'), true);
          self.deleteConfirm = false;
        });
    },
    updateReadNum(talk, num) {
      num = num ? num : 0;
      var cmid = this.cidToCmid(talk.cid);
      if (!cmid) return console.error(`there is no cmid for cid ${talk.cid}`);
      this.$store.commit('TALK_UPDATE_READ', { id: talk.id, num: num, cmid: cmid });
    },
    fmtDate(ymd) {
      return this.diffDateFormat(this.$moment(String(ymd), 'YYYYMMDD'));
    },
    isReadLine(msgs, idx) {
      if (this.readNum <= 0) return false;
      if (idx == 0) return this.readNum == 0;
      return msgs[idx].num > this.readNum && this.readNum >= msgs[idx - 1].num;
    },
    fmtTime(unix) {
      return this.$moment.unix(unix).format('H:mm');
    },
    calcScroll() {
      if (!this.isAndroid) return;
      var ele = this.msgsArea;
      this.scrollPosition = ele.scrollTop;
      var p = Math.round((100 * ele.scrollTop) / (ele.scrollHeight - ele.clientHeight));
      this.scrollPercent = p < 0 ? 0 : p > 100 ? 100 : p;
    },
    checkScroll() {
      if (!this.isAndroid) return;
      var ele = this.msgsArea;
      if (!ele) return (this.scrollYExists = false);
      this.scrollYExists = ele.clientHeight < ele.scrollHeight;
    },
    openEditEventDialog(data) {
      const calEvent = data.item;

      this.editEventData = {
        calEvent: calEvent,
        members: data.members,
        targetMoment: null,
      };
      this.editEventDialog = true;
    },
    ...mapActions({
      setTalkListener: 'setTalkListener',
    }),
  },
};
</script>

<style scoped>
.c-header-wrap {
  position: sticky;
  top: 79px;
  z-index: 5;
  height: 60px;
  background-color: #fff;
  z-index: 5;
}
.le-tbs .c-header-wrap {
  position: fixed;
  top: 48px;
  left: 0;
  right: 0;
  height: 48px;
  width: calc(100% - 220px);
}
.le-tbs .wide .c-header-wrap {
  width: 100%;
}
.chat-box {
  position: relative;
  height: calc(100vh - 162px);
}
.le-tbs .chat-box {
  margin-top: 48px;
  height: calc(100vh - 156px);
}
.le-sp .chat-box {
  height: initial !important;
}
.msgs-area {
  overflow-y: visible !important;
  overflow-x: hidden !important;
  background-color: #eeeeee;
  height: calc(100vh - 282px);
}
.le-tbs .msgs-area {
  position: fixed;
  top: 96px;
  left: 0;
  right: 0;
  bottom: 122px;
  height: initial;
}
.chat-input-area {
  position: absolute;
  background-color: #eeeeee;
  height: 62px;
  bottom: 0;
  left: 0;
  width: 100%;
}
.le-tbs .chat-input-area {
  position: fixed;
  bottom: 60px;
}

.c-avatar {
  min-width: 40px;
  max-width: 40px;
  text-align: center;
}

.c-talk-icon {
  width: 30px !important;
  height: 30px !important;
  margin-left: 10px;
  cursor: hand;
  cursor: pointer;
}
.c-talk-icon img {
  width: 30px !important;
  height: 30px !important;
}
.c-talk-name {
  font-weight: 600;
  text-shadow: 1px 1px 1px rgba(0, 0, 0, 0.28);
  font-size: 18px;
  padding-left: 5px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  cursor: hand;
  cursor: pointer;
}
.c-talk-name.noselect {
  color: rgba(0, 0, 0, 0.28);
  font-weight: 400;
  padding-left: 20px;
}
.c-btn-edit {
  margin-right: -10px;
}
.c-edit-btn i {
  font-size: 20px;
}
.c-btn-menu {
  margin-right: 5px;
  margin-left: -10px;
}

.c-unread-line {
  width: 100%;
  height: 16px;
  display: -webkit-box;
  display: -ms-flexbox;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 10px;
}
.c-unread-msg {
  font-size: 12px;
  font-weight: 600;
  margin: 0 20px;
}
.c-date-line {
  width: 100%;
  height: 18px;
  font-size: 12px;
  color: #fff !important;
  color: rgba(0, 0, 0, 0.24);
  background-color: rgba(0, 0, 0, 0.28);
  display: flex;
  justify-content: center;
  align-items: center;
  position: sticky;
  top: 0px;
  margin-bottom: 9px;
  z-index: 1;
  padding-top: 2px;
}
.c-msg-area {
  display: flex;
  margin: 0 5px 0 5px;
  padding-bottom: 10px;
}
.c-name {
  font-size: 10px;
  margin: -1px 0 1px 1px;
  color: rgba(0, 0, 0, 0.54);
}
.c-msg-area.self {
  flex-direction: row-reverse;
}
.c-msg-area.self .c-read-count {
  text-align: right;
  font-size: 10px;
}
.c-msg-area.self .c-time {
  text-align: right;
}
.c-msg-area.other {
  flex-direction: row;
}
.c-msg-append {
  margin: 0 6px -3px 6px;
  font-size: 11px;
  color: rgba(0, 0, 0, 0.54);
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
}
.c-none {
  display: flex;
  align-items: center;
  justify-content: center;
  height: calc(100vh - 300px);
  min-height: 300px;
  width: 100%;
  font-size: 20px;
  color: rgba(0, 0, 0, 0.54);
  text-align: center;
}
.le-tbx .c-none {
  height: calc(100vh - 225px);
}
.chat-box .loadingWrap {
  background-color: rgba(0, 0, 0, 0);
}
/* Normal flex item (not absolutely positioned) so the v-btn round hover background renders cleanly. */
.c-attach-btn {
  margin: 15px 0 0 6px;
}
/* extra gap between the last (image) icon and the message input */
.c-attach-btn-last {
  margin-right: 12px;
}
.c-attach-btn i {
  font-size: 24px;
}
.c-input {
  bottom: 0;
  position: absolute;
  /* the input is absolutely positioned at its flow position (after the icons); Vuetify overrides
     margin, so the left gap comes from .c-attach-btn-last's margin-right. width keeps the send gap. */
  width: calc(100% - 162px);
}
/* When the +/image attach buttons are hidden (general member of a publicity_member=0 community),
   the input starts at the left edge — drop the ~90px left-icon reservation so it fills the row. */
.no-attach .c-input {
  left: 12px;
  width: calc(100% - 84px);
}
.c-send-btn {
  margin: 15px 10px 0 0;
}
.c-send-btn i {
  margin: -1px 0 0 6px;
}

.c-members {
  position: absolute;
  top: 60px;
  left: 0px;
  width: 100%;
  padding: 5px 3px 1px 10px;
  background-color: #e0e0e0;
}
.le-tbs .c-members {
  top: 48px;
}
.msgs-loading {
  position: absolute;
  top: 80px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 100;
  opacity: 0.54;
}
.le-tbs .msgs-loading {
  top: 30px;
}
</style>
